import * as React from 'react';
import hoistNonReactMethods from 'hoist-non-react-methods';

import { getDisplayName } from './utils/getDisplayName';
import { st, classes } from './Focusable.st.css';
import { isStatelessComponent } from './utils/isStatelessComponent';
import { shellClasses } from '../../../../../common/shellClasses';
import { inputMethod } from './utils/inputMethod';
import classNames from 'classnames';

/**
 * Assigned the given propTypes to the given class.
 *
 * This is a hack because since Yoshi3, with babel-preset-yoshi,
 * the babel-plugin-transform-react-remove-prop-types is enabled and removes propTypes.
 *
 * So if we simply do FocusableHOC.propTypes = Component.propTypes, it is being stripped away.
 *
 * This later becomes a problem if another component defines:
 * <code>
 * Comp.propTypes = {
 *   prop1: SomeFocusableComp.propTypes.prop1
 * }
 * </code>
 */
const assignPropTypesHack = (target: any, propTypes: any) => {
  target.propTypes = propTypes;
};

type FocusableOptions = {
  isFocusWithMouse?: boolean;
  hasCustomFocus?: boolean;
  ignoreFocus?: boolean;
};

export type Triggers = {
  blur: () => void;
  focus: () => void;
};

export type WithFocusableProps = {
  className?: string;
  disabled?: boolean;
  onBlur?: (event: React.FocusEvent, triggers: Triggers) => void;
  onFocus?: (event: React.FocusEvent, triggers: Triggers) => void;
};

export interface FocusableRef {
  focus?: any;
}

export interface WithFocusableRefProps {
  ref?: React.RefObject<FocusableRef>;
}

export type InjectedFocusableProps<P extends {}, E = FocusableRef> = P & {
  // ref?: React.RefObject<E>; // it was ref object origanlly
  ref?: React.LegacyRef<E>;
  className?: string;
  /** handler from Focusable hoc */
  onBlur?: React.FocusEventHandler<E>;
  /** handler from Focusable hoc */
  onFocus?: React.FocusEventHandler<E>;
};

/*
 * TODO: Consider adding 'disabled' state to this HOC, since:
 * - When component is focused and then it becomes disabled, then the focus needs to be blured.
 *
 * TODO: Consider using [Recompose](https://github.com/acdlite/recompose/tree/master/src/packages/recompose) to do:
 *  - the static hoisting
 *  - set displayName
 */
export function withFocusable<P extends {}, PP extends P & WithFocusableProps>(
  Component: React.FunctionComponent<InjectedFocusableProps<P>>,
  options?: FocusableOptions,
): React.FunctionComponent<PP>;

export function withFocusable<P extends {}, PP extends P & WithFocusableProps>(
  Component: React.ComponentClass<InjectedFocusableProps<P>>,
  options?: FocusableOptions,
): React.FunctionComponent<PP & WithFocusableRefProps>;

export function withFocusable<P extends {}, PP extends P & WithFocusableProps>(
  Component: React.ComponentType<InjectedFocusableProps<P>>,
  options: FocusableOptions = { hasCustomFocus: false },
): React.FunctionComponent<PP> | React.FunctionComponent<PP> {
  interface FocusableState {
    focus: boolean;
    focusVisible: boolean;
  }

  class Focusable extends React.Component<PP, FocusableState> {
    static displayName = getDisplayName(Component);
    static defaultProps = Component.defaultProps;

    wrappedComponentRef: FocusableRef = null;

    state: FocusableState = {
      focus: false,
      focusVisible: false,
    };

    componentWillUnmount() {
      inputMethod.unsubscribe(this);
    }

    componentDidUpdate(prevProps: WithFocusableProps) {
      /*
        in case when button was focused and then become disabled,
        we need to trigger blur logic and remove all listers, as disabled button
        do not trigger onFocus and onBlur events
      */
      const isFocused = this.state.focus || this.state.focusVisible;
      const isBecomeDisabled = !prevProps.disabled && this.props.disabled;
      if (isFocused && isBecomeDisabled) {
        this.onBlur({} as React.FocusEvent);
      }
    }

    focus = () => {
      if (this.wrappedComponentRef?.focus) {
        this.wrappedComponentRef.focus();
      }
    };

    markAsFocused = () => {
      this.setState({
        focus: true,
        focusVisible: options.isFocusWithMouse || inputMethod.isKeyboard(),
      });
      inputMethod.subscribe(this, () => {
        if (options.isFocusWithMouse || inputMethod.isKeyboard()) {
          this.setState({ focusVisible: true });
        }
      });
    };

    markAsBlurred = () => {
      inputMethod.unsubscribe(this);
      this.setState({ focus: false, focusVisible: false });
    };

    getTriggers(): Triggers {
      return { blur: this.markAsBlurred, focus: this.markAsFocused };
    }

    onFocus: React.FocusEventHandler = (event) => {
      const { onFocus } = this.props;
      onFocus ? onFocus(event, this.getTriggers()) : this.markAsFocused();
    };

    onBlur: React.FocusEventHandler = (event) => {
      const { onBlur } = this.props;
      onBlur ? onBlur(event, this.getTriggers()) : this.markAsBlurred();
    };

    render() {
      const reference = isStatelessComponent(Component)
        ? undefined
        : (ref: FocusableRef) => {
            this.wrappedComponentRef = ref;
          };

      const { className, onBlur, onFocus, ...rest } = this.props;

      return (
        <Component
          {...(rest as any)}
          ref={reference}
          className={st(
            classes.root,
            {
              focus: this.state.focus,
              'focus-visible': this.state.focusVisible,
            },
            className,
            classNames({
              [shellClasses.hasCustomFocus]: options.hasCustomFocus,
              [shellClasses.ignoreFocus]: options.ignoreFocus,
            }),
          )}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          {...{
            'data-focusable-focus': this.state.focus,
            'data-focusable-focus-visible': this.state.focusVisible,
          }}
        />
      );
    }
  }

  // eslint-disable-next-line react/forbid-foreign-prop-types
  assignPropTypesHack(Focusable, Component.propTypes);

  return isStatelessComponent(Component)
    ? Focusable
    : hoistNonReactMethods(Focusable, Component, {
        delegateTo: (c: Focusable) => c.wrappedComponentRef,
        hoistStatics: true,
      });
}
